// Backlog 086 — fs-backed ServiceFolderReader factory for CLI preflight.
//
// The shared `validateConfig` runs the type↔folder alignment check whenever
// `opts.serviceReader` is provided. The CLI preflight feeds it this fs-based
// reader so files at `<projectRoot>/services/<svcName>/<rel>` are inspected
// before the tarball is built.
//
// The reader caps reads at 1MB — a `package.json` larger than that is
// pathological, and we don't want to slurp megabytes for an alignment hint.
// Above the cap, `readText` returns null and the alignment predicate falls
// back to a soft-pass.

import { existsSync, readFileSync, statSync } from 'fs';
import { join } from 'path';

import type { ServiceFolderReader } from '../../../shared/src/index.js';

const READ_CAP_BYTES = 1024 * 1024;

export function fsServiceFolderReader(serviceDir: string): ServiceFolderReader {
  return {
    has(rel) {
      const full = join(serviceDir, rel);
      return existsSync(full);
    },
    readText(rel) {
      const full = join(serviceDir, rel);
      if (!existsSync(full)) return null;
      try {
        const stat = statSync(full);
        if (!stat.isFile()) return null;
        if (stat.size > READ_CAP_BYTES) return null;
        return readFileSync(full, 'utf-8');
      } catch {
        return null;
      }
    },
  };
}

/**
 * Build a per-service reader factory rooted at `projectRoot/services/`. When
 * a service folder doesn't exist on disk, returns null — `validateConfig`
 * skips alignment for that service rather than firing a noisy false-positive.
 */
export function makeFsServiceReaderFactory(
  projectRoot: string,
): (serviceName: string) => ServiceFolderReader | null {
  return (serviceName: string) => {
    const dir = join(projectRoot, 'services', serviceName);
    if (!existsSync(dir)) return null;
    return fsServiceFolderReader(dir);
  };
}
